// Subscribe to worker progress events and dispatch into the
// upload-queue Zustand store. Mounted once at the top of AppShell
// so the subscription lives for the lifetime of the page.

import * as Comlink from "comlink";
import { useEffect } from "react";
import { useVault } from "./useVault";
import { useUploadQueue } from "../stores/uploadQueue";
import type { ProgressEvent } from "../worker/api";

export function useProgressSubscription(): void {
  const worker = useVault();

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;

    const cb = (ev: ProgressEvent) => {
      const store = useUploadQueue.getState();
      switch (ev.type) {
        case "import-start":
          store.enqueue({
            taskId: ev.taskId,
            fileName: ev.fileName,
            totalBytes: ev.totalBytes,
            bytesDone: 0,
            status: "running",
          });
          break;
        case "import-chunk":
          store.update(ev.taskId, { bytesDone: ev.bytesDone });
          break;
        case "import-done":
          store.update(ev.taskId, { status: "done" });
          break;
        case "import-error":
          store.update(ev.taskId, { status: "error", error: ev.error });
          break;
        // Export events use the same queue model (progress only; the
        // ExportDialog owns the destination handle itself).
        case "export-start":
          store.enqueue({
            taskId: ev.taskId,
            fileName: ev.fileName,
            totalBytes: ev.totalBytes,
            bytesDone: 0,
            status: "running",
          });
          break;
        case "export-chunk":
          store.update(ev.taskId, { bytesDone: ev.bytesDone });
          break;
        case "export-done":
          store.update(ev.taskId, { status: "done" });
          break;
        case "export-error":
          store.update(ev.taskId, { status: "error", error: ev.error });
          break;
      }
    };

    void worker
      .subscribeProgress(Comlink.proxy(cb))
      .then((u) => {
        if (cancelled) {
          void u();
        } else {
          unsub = u;
        }
      });

    return () => {
      cancelled = true;
      if (unsub) void unsub();
    };
  }, [worker]);
}
